const BANNER_REVEAL_S = 2;
const BANNER_OVERLAY_FADE_S = 0.7;

/** Matches banner SCSS: horizontal align stacks below this width. */
const ALIGN_MOBILE_MAX_PX = 1024;

/** Shows `.banner__container-overlay` until loader reveal (see `_banner.scss`). */
export const BANNER_CONTENT_HIDDEN_CLASS = "banner__content--loader-hidden";

/** Added after loader reveal; overrides `body.has-loader` header hide (see `_header.scss`). */
export const HEADER_LOADER_REVEALED_CLASS = "site-header--loader-revealed";

function pageHasLoader() {
  return document.body.classList.contains("has-loader");
}

function isAlignMobileViewport() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${ALIGN_MOBILE_MAX_PX}px)`).matches
  );
}

/**
 * `left-aligned`/`right-aligned` place the wrapper with flex justification, which follows the
 * writing direction — so under `dir="rtl"` the banner sits on the opposite physical side while
 * the GSAP transform stays physical.
 * @param {HTMLElement} section
 */
function isRtlSection(section) {
  if (typeof window === "undefined") return false;
  return window.getComputedStyle(section).direction === "rtl";
}

/**
 * Off-screen translate for `.banner__container-wrapper` based on `section.banner` alignment.
 * Top/left/right aligned banners slide from the bottom on viewports ≤1024px (stacked layout).
 * @param {HTMLElement} section
 */
export function getBannerContainerExitOffset(section) {
  const fromBottom = { xPercent: 0, yPercent: 100 };
  const inlineStart = isRtlSection(section) ? 100 : -100;
  const useBottomRevealOnMobile =
    isAlignMobileViewport() &&
    (section.classList.contains("top-aligned") ||
      section.classList.contains("left-aligned") ||
      section.classList.contains("right-aligned"));

  if (useBottomRevealOnMobile) {
    return fromBottom;
  }

  if (section.classList.contains("bottom-aligned")) {
    return fromBottom;
  }
  if (section.classList.contains("top-aligned")) {
    return { xPercent: 0, yPercent: -100 };
  }
  if (section.classList.contains("left-aligned")) {
    return { xPercent: inlineStart, yPercent: 0 };
  }
  if (section.classList.contains("right-aligned")) {
    return { xPercent: -inlineStart, yPercent: 0 };
  }
  return fromBottom;
}

export function getLoaderBannerElements() {
  const section = document.querySelector("section.banner");
  if (!section) return null;

  const container = section.querySelector(".banner__container-wrapper");
  const overlay = section.querySelector(".banner__container-overlay");
  const header = document.querySelector(".site-header");
  if (!container || !overlay) return null;

  return { section, container, overlay, header };
}

/**
 * Place hero banner container off-screen with overlay covering content until reveal runs.
 * @param {import("gsap").GSAP | null} gsap
 */
export function setLoaderBannerInitialState(gsap) {
  const els = getLoaderBannerElements();
  if (!els) return null;

  const offset = getBannerContainerExitOffset(els.section);

  els.container.classList.add(BANNER_CONTENT_HIDDEN_CLASS);

  if (gsap) {
    gsap.set(els.container, offset);
    gsap.set(els.overlay, { opacity: 1 });
  } else {
    const { xPercent, yPercent } = offset;
    els.container.style.transform = `translate(${xPercent}%, ${yPercent}%)`;
  }

  els.section.classList.add("banner--loader-pending");
  return els;
}

/**
 * Slide `.banner__container-wrapper` into view, then fade out overlay and fade in header.
 * @param {import("gsap").GSAP} gsap
 * @param {{ prefersReduced?: boolean, onComplete?: () => void }} [options]
 */
export function runLoaderBannerReveal(gsap, options = {}) {
  const { prefersReduced = false, onComplete } = options;
  const els = getLoaderBannerElements();

  if (!els || !gsap) {
    onComplete?.();
    return;
  }

  const revealDur = prefersReduced ? 0.2 : BANNER_REVEAL_S;
  const overlayFadeDur = prefersReduced ? 0.1 : BANNER_OVERLAY_FADE_S;
  const revealEase = prefersReduced ? "none" : "power2.inOut";

  // Override `.has-loader .banner__container-wrapper { visibility: hidden }` for the slide-in.
  gsap.set(els.container, { visibility: "visible" });
  gsap.set(els.overlay, { opacity: 1 });

  const finishReveal = () => {
    els.section.classList.remove("banner--loader-pending");
    els.container.classList.remove(BANNER_CONTENT_HIDDEN_CLASS);
    gsap.set(els.overlay, { clearProps: "opacity" });
    onComplete?.();
  };

  const revealHeader =
    els.header && pageHasLoader();

  gsap.to(els.container, {
    xPercent: 0,
    yPercent: 0,
    duration: revealDur,
    ease: revealEase,
    onComplete: () => {
      const fadeEase = "power1.out";
      const fadeTimeline = gsap.timeline({ onComplete: finishReveal });

      fadeTimeline.to(els.overlay, {
        opacity: 0,
        duration: overlayFadeDur,
        ease: fadeEase,
      });

      if (revealHeader) {
        fadeTimeline.to(
          els.header,
          {
            autoAlpha: 1,
            duration: overlayFadeDur,
            ease: fadeEase,
            onComplete: () => {
              els.header.classList.add(HEADER_LOADER_REVEALED_CLASS);
              gsap.set(els.header, { clearProps: "opacity,visibility" });
            },
          },
          0
        );
      }
    },
  });
}

export function resetLoaderBannerWithoutAnimation() {
  const els = getLoaderBannerElements();
  if (!els) return;

  els.container.style.transform = "";
  els.container.style.visibility = "visible";
  els.container.classList.remove(BANNER_CONTENT_HIDDEN_CLASS);
  els.overlay.style.opacity = "";
  els.overlay.style.pointerEvents = "";
  if (els.header && pageHasLoader()) {
    els.header.classList.add(HEADER_LOADER_REVEALED_CLASS);
  }
  els.section.classList.remove("banner--loader-pending");
}
